import { CatchError } from 'Exceptions/exceptions'
import { EMPTY_API_CALLBACK_FORM } from 'Main/Constant'
import ApiCallbackService from 'Main/service/ApiCallbackService'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { getRefreshToken } from 'variables/common'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'


function ApiCallbackManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const apiCallbackService = useMemo(() => new ApiCallbackService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [modalMode, setModalMode] = useState(null)
  const [editingCallback, setEditingCallback] = useState(null)
  const [formData, setFormData] = useState(EMPTY_API_CALLBACK_FORM)
  const [submitting, setSubmitting] = useState(false)

  const getCallbacks = useCallback(() => apiCallbackService.getData(), [apiCallbackService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const openAdd = () => {
    setModalMode('add')
    setEditingCallback(null)
    setFormData(EMPTY_API_CALLBACK_FORM)
  }

  const openEdit = (callback) => {
    setModalMode('edit')
    setEditingCallback(callback)
    setFormData({
      device_name: callback.device_name || '',
      endpoint: callback.endpoint || '',
      token: callback.token || '',
      is_global_callback: Boolean(callback.is_global_callback)
    })
  }

  const closeModal = () => {
    if (!submitting) {
      setModalMode(null)
      setEditingCallback(null)
    }
  }

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    try {
      setSubmitting(true)
      if (modalMode === 'add') {
        await apiCallbackService.create(formData)
      } else {
        await apiCallbackService.update(editingCallback.id, formData)
      }
      setModalMode(null)
      setEditingCallback(null)
      await swal(
        'Success!',
        modalMode === 'add' ? 'API callback destination added.' : 'API callback destination updated.',
        'success'
      )
      setReloadKey((current) => current + 1)
    } catch (requestError) {
      const caught = CatchError(requestError, navigate, refreshToken)
      if (caught !== undefined) {
        await swal(
          modalMode === 'add' ? 'Unable to add' : 'Unable to update',
          requestError?.response?.data?.message || 'The API callback destination could not be saved.',
          'error'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: 'Delete API callback destination?',
      text: 'Artapay will stop sending events to this destination.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true
    })

    if (!confirmed) return

    try {
      await apiCallbackService.deleteById(id)
      await swal('Success!', 'API callback destination deleted.', 'success')
      setReloadKey((current) => current + 1)
    } catch (requestError) {
      const caught = CatchError(requestError, navigate, refreshToken)
      if (caught !== undefined) {
        await swal(
          'Unable to delete',
          requestError?.response?.data?.message || 'The API callback destination could not be deleted.',
          'error'
        )
      }
    }
  }

  const columns = [
    {
      header: 'Application',
      accessorKey: 'device_name',
      maxWidth: 250,
      cell: ({ getValue }) => <strong>{getValue() || 'Unnamed application'}</strong>
    },
    {
      header: 'Route type',
      accessorKey: 'callbackType',
      maxWidth: 150,
      cell: ({ row, getValue }) => <span className="text-primary">{getValue() || (row.original.is_global_callback ? 'Global' : 'Device')}</span>
    },
    {
      header: 'Endpoint',
      accessorKey: 'endpoint',
      maxWidth: 300,
      cell: ({ getValue }) => <code title={getValue()}>{getValue()}</code>
    },
    {
      header: 'Authorization',
      accessorKey: 'token',
      cell: ({ getValue }) => getValue()
        ? <span><i className="fa-solid fa-lock me-1" />Token configured</span>
        : <span className="text-muted">No token</span>
    },
    { header: 'Created', accessorKey: 'date' },
    {
      header: 'Actions',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      maxWidth: 130,
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-primary" onClick={() => openEdit(row.original)} title="Edit API callback destination">
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original.id)} title="Delete API callback destination">
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      )
    }
  ]

  const menu = (
    <div className="text-center">
      <button type="button" className="btn btn-success" onClick={openAdd} title="Add API callback destination">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Add API Callback
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="API callback Config"
          subtitle=""
          columns={columns}
          getData={getCallbacks}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading..."
          emptyLabel="No callback config configured."
          onError={handleListError}
        />
      </section>

      {modalMode && (
        <>
          <div className="modal-backdrop fade show" onClick={submitting ? undefined : closeModal} />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {modalMode === 'add' ? 'Add API callback destination' : 'Edit API callback destination'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={submitting}
                    />
                  </div>

                  <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label htmlFor="callback-device-name" className="form-label">Application name</label>
                      <input
                        id="callback-device-name"
                        name="device_name"
                        className="form-control"
                        value={formData.device_name}
                        onChange={handleInputChange}
                        placeholder="Main store webhook"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="callback-endpoint" className="form-label">Endpoint URL</label>
                      <input
                        id="callback-endpoint"
                        name="endpoint"
                        type="url"
                        className="form-control"
                        value={formData.endpoint}
                        onChange={handleInputChange}
                        placeholder="https://example.com/payments/webhook"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="callback-token" className="form-label">
                        Authorization token <span className="text-muted">(optional)</span>
                      </label>
                      <textarea
                        id="callback-token"
                        name="token"
                        className="form-control"
                        rows="3"
                        value={formData.token}
                        onChange={handleInputChange}
                        placeholder="Sent as the Authorization header"
                      />
                    </div>

                    <div className="form-check">
                      <input
                        id="callback-global"
                        name="is_global_callback"
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.is_global_callback}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="callback-global" className="form-check-label">
                        Receive callbacks for every payment channel
                      </label>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-light text-black" onClick={closeModal} disabled={submitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success" disabled={submitting}>
                      <i className={`fa-solid ${submitting ? 'fa-circle-notch fa-spin' : 'fa-floppy-disk'}`} />{' '}
                      {submitting ? 'Saving...' : (modalMode === 'add' ? 'Add API Callback' : 'Save Changes')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ApiCallbackManager
